import { parseCsvRecords } from './parseCsvRow';
import { resolveNativeLanguage } from './nativeLanguage';

export type WidaDomainKey = 'listening' | 'speaking' | 'reading' | 'writing' | 'composite';

export type AssessmentScale = 'access' | 'alternate_access';

export type DomainScore = {
  scale: number | null;
  level: number | null;
};

export type WidaYearRecord = {
  year: number;
  grade: string;
  school: string;
  tier: string;
  testDate: string;
  waived: boolean;
  yearsInProgram: number | null;
  listening: DomainScore;
  speaking: DomainScore;
  reading: DomainScore;
  writing: DomainScore;
  composite: DomainScore;
  assessmentScale?: AssessmentScale;
};

export type WidaStudent = {
  id: string;
  stateStudentId: string;
  localStudentId: string;
  firstName: string;
  middleName: string;
  lastName: string;
  fullName: string;
  nativeLanguage: string;
  district: string;
  districtState: string;
  latestGrade: string;
  latestSchool: string;
  yearsInEl: number | null;
  years: WidaYearRecord[];
};

const COLUMNS = {
  state: ['state student id'],
  local: ['local student id', 'district student id'],
  first: ['first name', 'student first name'],
  middle: ['middle name', 'student middle name'],
  last: ['last name', 'student last name'],
  grade: ['grade'],
  tier: ['tier', 'reported tier'],
  school: ['school name'],
  district: ['district name'],
  districtNumber: ['district number'],
  nativeLanguage: ['native language'],
  yearsInProgram: ['length of time in lep/ell program'],
  participation: ['participation status'],
  testDate: ['test date', 'test completion date'],
  listeningScale: ['listening scale score', 'scale score - listening'],
  listeningLevel: ['listening proficiency level', 'proficiency level - listening'],
  speakingScale: ['speaking scale score', 'scale score - speaking'],
  speakingLevel: ['speaking proficiency level', 'proficiency level - speaking'],
  readingScale: ['reading scale score', 'scale score - reading'],
  readingLevel: ['reading proficiency level', 'proficiency level - reading'],
  writingScale: ['writing scale score', 'scale score - writing'],
  writingLevel: ['writing proficiency level', 'proficiency level - writing'],
  compositeScale: ['overall composite scale score', 'scale score - overall'],
  compositeLevel: ['overall composite proficiency level', 'proficiency level - overall'],
  doNotScoreListening: ['do not score code - listening'],
  doNotScoreReading: ['do not score code - reading'],
  doNotScoreSpeaking: ['do not score code - speaking'],
  doNotScoreWriting: ['do not score code - writing'],
} as const;

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

function toNumber(value: string | undefined): number | null {
  if (value == null) return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

const EARLIEST_PLAUSIBLE_TEST_YEAR = 1990;

function parseYear(testDate: string): number | null {
  const match = testDate.match(/\d{4}/);
  if (!match) return null;
  const year = Number(match[0]);
  if (year < EARLIEST_PLAUSIBLE_TEST_YEAR || year > new Date().getFullYear() + 1) return null;
  return year;
}

function formatMiddle(middle: string): string {
  const value = middle.trim();
  if (value === '') return '';
  if (value.length === 1) return `${value}.`;
  return value;
}

function buildFullName(first: string, middle: string, last: string): string {
  return [first.trim(), formatMiddle(middle), last.trim()].filter(Boolean).join(' ');
}

function stateFromDistrictNumber(districtNumber: string): string {
  const prefix = districtNumber.trim().match(/^[A-Za-z]{2}/);
  return prefix ? prefix[0].toUpperCase() : '';
}

type Getter = (key: keyof typeof COLUMNS) => string;

function buildHeaderIndex(headers: string[]): Map<string, number> {
  const index = new Map<string, number>();
  headers.forEach((header, i) => {
    if (!index.has(header)) index.set(header, i);
  });
  return index;
}

function makeGetter(headerIndex: Map<string, number>, row: string[]): Getter {
  return (key) => {
    for (const name of COLUMNS[key]) {
      const idx = headerIndex.get(name);
      if (idx != null) return row[idx] ?? '';
    }
    return '';
  };
}

function applyLatest(student: WidaStudent): void {
  student.years.sort((a, b) => a.year - b.year);
  const latest = student.years[student.years.length - 1];
  if (latest) {
    student.latestGrade = latest.grade;
    student.latestSchool = latest.school;
    student.yearsInEl = latest.yearsInProgram;
  }
}

function isDoNotScore(get: Getter, key: keyof typeof COLUMNS): boolean {
  return get(key).trim() !== '';
}

function domainScore(
  get: Getter,
  scaleKey: keyof typeof COLUMNS,
  levelKey: keyof typeof COLUMNS,
  doNotScoreKey?: keyof typeof COLUMNS,
): DomainScore {
  if (doNotScoreKey && isDoNotScore(get, doNotScoreKey)) return { scale: null, level: null };
  return { scale: toNumber(get(scaleKey)), level: toNumber(get(levelKey)) };
}

export function latestAdministrationYear(students: WidaStudent[]): number | null {
  let max: number | null = null;
  for (const student of students) {
    if (!Array.isArray(student.years)) continue;
    for (const record of student.years) {
      if (max == null || record.year > max) max = record.year;
    }
  }
  return max;
}

export function assessmentScaleOf(record: Pick<WidaYearRecord, 'assessmentScale'>): AssessmentScale {
  return record.assessmentScale ?? 'access';
}

export function scaleForUploadKind(kind: string): AssessmentScale {
  return kind === 'alternate_access_wida' ? 'alternate_access' : 'access';
}

function isAlternateScale(record: WidaYearRecord): boolean {
  return assessmentScaleOf(record) === 'alternate_access';
}

export function accessScaleOnly(wida: WidaStudent[]): WidaStudent[] {
  const kept: WidaStudent[] = [];
  for (const student of wida) {
    if (!Array.isArray(student.years)) {
      kept.push(student);
      continue;
    }
    const years = student.years.filter((y) => !isAlternateScale(y));
    if (years.length === student.years.length) {
      kept.push(student);
      continue;
    }
    if (years.length === 0) continue;
    const trimmed = { ...student, years };
    applyLatest(trimmed);
    kept.push(trimmed);
  }
  return kept;
}

export function parseWidaHistory(
  csvText: string,
  assessmentScale: AssessmentScale = 'access',
): WidaStudent[] {
  const records = parseCsvRecords(csvText);
  if (records.length < 2) return [];

  const headers = (records[0] ?? []).map(normalize);
  const headerIndex = buildHeaderIndex(headers);
  const byStudent = new Map<string, WidaStudent>();
  const latestState = new Map<string, { year: number; state: string }>();

  for (let i = 1; i < records.length; i++) {
    const row = records[i] ?? [];
    const get = makeGetter(headerIndex, row);

    const stateStudentId = get('state').trim();
    const localStudentId = get('local').trim();
    const id = stateStudentId || localStudentId;
    if (id === '') continue;

    const year = parseYear(get('testDate'));
    if (year == null) continue;

    const rowState = stateFromDistrictNumber(get('districtNumber'));
    if (rowState !== '') {
      const seen = latestState.get(id);
      if (!seen || year >= seen.year) latestState.set(id, { year, state: rowState });
    }

    const waived =
      normalize(get('participation')) === 'waived' ||
      (isDoNotScore(get, 'doNotScoreListening') &&
        isDoNotScore(get, 'doNotScoreReading') &&
        isDoNotScore(get, 'doNotScoreSpeaking') &&
        isDoNotScore(get, 'doNotScoreWriting'));

    const record: WidaYearRecord = {
      year,
      grade: get('grade').trim(),
      school: get('school').trim(),
      tier: get('tier').trim(),
      testDate: get('testDate').trim(),
      waived,
      yearsInProgram: toNumber(get('yearsInProgram')),
      listening: domainScore(get, 'listeningScale', 'listeningLevel', 'doNotScoreListening'),
      speaking: domainScore(get, 'speakingScale', 'speakingLevel', 'doNotScoreSpeaking'),
      reading: domainScore(get, 'readingScale', 'readingLevel', 'doNotScoreReading'),
      writing: domainScore(get, 'writingScale', 'writingLevel', 'doNotScoreWriting'),
      composite: domainScore(get, 'compositeScale', 'compositeLevel'),
      assessmentScale,
    };

    const existing = byStudent.get(id);
    if (existing) {
      existing.years.push(record);
      continue;
    }

    byStudent.set(id, {
      id,
      stateStudentId,
      localStudentId,
      firstName: get('first').trim(),
      middleName: get('middle').trim(),
      lastName: get('last').trim(),
      fullName: buildFullName(get('first'), get('middle'), get('last')),
      nativeLanguage: resolveNativeLanguage(get('nativeLanguage')),
      district: get('district').trim(),
      districtState: '',
      latestGrade: '',
      latestSchool: '',
      yearsInEl: null,
      years: [record],
    });
  }

  const students = [...byStudent.values()];
  for (const student of students) {
    applyLatest(student);
    student.districtState = latestState.get(student.id)?.state ?? '';
  }
  return students;
}

export function mergeWidaRosters(students: WidaStudent[]): WidaStudent[] {
  const byId = new Map<string, WidaStudent>();
  for (const student of students) {
    const existing = byId.get(student.id);
    if (!existing) {
      byId.set(student.id, { ...student, years: [...student.years] });
      continue;
    }
    for (const year of student.years) {
      const at = existing.years.findIndex((y) => y.year === year.year);
      if (at === -1) {
        existing.years.push(year);
        continue;
      }
      if (
        assessmentScaleOf(year) === 'alternate_access' &&
        assessmentScaleOf(existing.years[at]!) === 'access'
      ) {
        existing.years[at] = year;
      }
    }
    if (existing.districtState === '' && student.districtState !== '') {
      existing.districtState = student.districtState;
    }
  }
  const merged = [...byId.values()];
  for (const student of merged) applyLatest(student);
  return merged;
}
